(() => {
  const normalizeCategory = (value) => value.toLowerCase().replace(/&amp;/g, '&').trim();
  const normalizeUpgradeText = (value = '') => value
    .replace(/&amp;/gi, '&')
    .replace(/[\u00a0\u2000-\u200d\u202f\u205f\u3000\ufeff]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const UPGRADE_CATEGORY_PATTERN = /^(?:what have you customized on your )?(?:interior|exterior\s*(?:&|and)\s*armor|suspension\s*(?:&|and)\s*chassis|drivetrain(?:\s*\(transmission\))?|engine|wheels\s*(?:&|and)\s*tires)(?:\s+upgrades?)?\??$/i;
  const CURRENT_UPGRADES_PATTERN = /^current upgrades?$/i;
  const PLACEHOLDER_RESPONSE_PATTERN = /^(?:n\/?a|none(?: yet)?|nothing|no(?:ne)?|nope|unknown|tbd|null|not applicable)$/i;
  const IMAGE_FILENAME_PATTERN = /^[^\\/:*?"<>|\r\n]+\.(?:avif|bmp|gif|heic|heif|jpe?g|png|tiff?|webp)$/i;

  const isUpgradeCategory = (value) => {
    const normalized = normalizeUpgradeText(value);
    return UPGRADE_CATEGORY_PATTERN.test(normalized) || CURRENT_UPGRADES_PATTERN.test(normalized);
  };

  const isMeaningfulUpgradeResponse = (value) => {
    const normalized = normalizeUpgradeText(value);
    if (!normalized) return false;
    if (/^[\p{P}\p{S}_]+$/u.test(normalized)) return false;
    if (/^(?:0+(?:\.0+)?|1+(?:\.0+)?)$/.test(normalized)) return false;
    if (IMAGE_FILENAME_PATTERN.test(normalized)) return false;
    return !PLACEHOLDER_RESPONSE_PATTERN.test(normalized);
  };

  class CustomerShowcasePage extends HTMLElement {
    connectedCallback() {
      this.setupGallery();
      this.setupUpgradeCards();
      this.setupTitleFitting();
    }

    disconnectedCallback() {
      if (this.handleTextResize) window.removeEventListener('resize', this.handleTextResize);
      this.textResizeObserver?.disconnect();
      if (this.textFitFrame) cancelAnimationFrame(this.textFitFrame);
    }

    setupGallery() {
      const slides = [...this.querySelectorAll('[data-customer-gallery-slide]')];
      if (slides.length < 2) return;

      const dots = [...this.querySelectorAll('[data-customer-gallery-dot]')];
      const previous = this.querySelector('[data-customer-gallery-previous]');
      const next = this.querySelector('[data-customer-gallery-next]');
      let activeIndex = 0;

      const showSlide = (index) => {
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === activeIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
        dots.forEach((dot, dotIndex) => {
          const isActive = dotIndex === activeIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      };

      previous?.addEventListener('click', () => showSlide(activeIndex - 1));
      next?.addEventListener('click', () => showSlide(activeIndex + 1));
      dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => showSlide(dotIndex)));
      showSlide(0);
    }

    setupTitleFitting() {
      const elements = [...this.querySelectorAll('[data-customer-fit-text]')];
      if (!elements.length) return;

      const fit = (element) => {
        const mobile = window.matchMedia('(max-width: 767px)').matches;
        const maximum = Number(mobile ? element.dataset.mobileMax : element.dataset.desktopMax) || (mobile ? 46 : 62);
        const minimum = Number(element.dataset.minimum) || 22;
        element.style.fontSize = `${maximum}px`;

        const availableWidth = element.clientWidth;
        const renderedWidth = element.scrollWidth;
        if (!availableWidth || renderedWidth <= availableWidth) return;

        let fitted = Math.floor((maximum * availableWidth * 0.98) / renderedWidth);
        fitted = Math.max(minimum, Math.min(maximum, fitted));
        element.style.fontSize = `${fitted}px`;

        while (fitted > minimum && element.scrollWidth > element.clientWidth) {
          fitted -= 1;
          element.style.fontSize = `${fitted}px`;
        }
      };

      const fitAll = () => elements.forEach(fit);
      const scheduleFit = () => {
        if (this.textFitFrame) cancelAnimationFrame(this.textFitFrame);
        this.textFitFrame = requestAnimationFrame(() => {
          this.textFitFrame = null;
          fitAll();
        });
      };

      scheduleFit();
      if (document.fonts?.ready) document.fonts.ready.then(scheduleFit);

      this.textResizeObserver = new ResizeObserver(scheduleFit);
      elements.forEach((element) => this.textResizeObserver.observe(element.parentElement || element));

      this.handleTextResize = scheduleFit;
      window.addEventListener('resize', this.handleTextResize, { passive: true });
    }

    setupUpgradeCards() {
      const source = this.querySelector('[data-customer-upgrade-source]');
      const target = this.querySelector('[data-customer-upgrade-grid]');
      if (!source || !target) {
        this.updateUpgradeVisibility(false);
        return;
      }
      target.replaceChildren();
      this.storedUpgradeCopy = this.readStoredUpgradeCopy();

      const richText = source.querySelector('.metafield-rich_text_field') || source;
      const children = [...richText.children];
      const configuredPresentation = this.dataset.customerUpgradePresentation || 'auto';
      const narrativeParagraphs = [...richText.querySelectorAll('p')]
        .filter((paragraph) => isMeaningfulUpgradeResponse(normalizeUpgradeText(paragraph.textContent)));
      const narrativeText = normalizeUpgradeText(
        narrativeParagraphs.map((paragraph) => paragraph.textContent).join(' ')
      );
      const narrativeWordCount = narrativeText ? narrativeText.split(/\s+/).length : 0;
      const hasUpgradeLists = Boolean(richText.querySelector('ul, ol'));
      const useNarrativePresentation = configuredPresentation === 'narrative'
        || (
          configuredPresentation === 'auto'
          && !hasUpgradeLists
          && narrativeParagraphs.length > 0
          && (narrativeWordCount >= 22 || narrativeText.length >= 140)
        );

      if (useNarrativePresentation) {
        const heading = this.querySelector('.customer-showcase-upgrades__heading');
        if (heading) heading.textContent = this.dataset.customerNarrativeHeading || 'ABOUT THIS BUILD';

        this.classList.add('customer-showcase-upgrades--narrative');
        this.classList.remove('customer-showcase-upgrades--compact');
        target.classList.add('customer-showcase-upgrades__grid--narrative');
        target.classList.remove('customer-showcase-upgrades__grid--compact');
        target.append(this.createNarrativeBuildStory(narrativeParagraphs, narrativeText));
        this.closest('.dpp-build-gallery--customer-upgrades')?.classList.add('dpp-build-gallery--customer-narrative');
        this.updateUpgradeVisibility(true);
        return;
      }

      this.classList.remove('customer-showcase-upgrades--narrative');
      target.classList.remove('customer-showcase-upgrades__grid--narrative');
      this.closest('.dpp-build-gallery--customer-upgrades')?.classList.remove('dpp-build-gallery--customer-narrative');

      let category = '';
      const entries = [];

      children.forEach((element) => {
        if (/^H[1-6]$/.test(element.tagName) || element.tagName === 'P') {
          const possibleCategory = element.textContent.trim().replace(/:\s*$/, '');
          const next = element.nextElementSibling;
          if (possibleCategory && next && ['UL', 'OL'].includes(next.tagName)) category = possibleCategory;
          return;
        }

        if (!['UL', 'OL'].includes(element.tagName) || !isUpgradeCategory(category)) return;
        [...element.children].forEach((item) => {
          const raw = normalizeUpgradeText(item.textContent);
          if (!raw) return;
          const separator = raw.indexOf(':');
          const isCurrentUpgrades = CURRENT_UPGRADES_PATTERN.test(normalizeUpgradeText(category));
          if (separator < 1 && !isCurrentUpgrades) return;
          const title = separator > 0 ? normalizeUpgradeText(raw.slice(0, separator)) : raw;
          const response = separator > 0 ? normalizeUpgradeText(raw.slice(separator + 1)) : raw;
          if (!title || !isMeaningfulUpgradeResponse(response)) return;
          entries.push({ category, title, response, isCurrentUpgrades });
        });
      });

      const compactThreshold = Number.parseInt(this.dataset.customerCompactThreshold || '12', 10);
      const useCompactPresentation = configuredPresentation === 'compact'
        || (configuredPresentation === 'auto' && entries.length > compactThreshold);

      this.classList.toggle('customer-showcase-upgrades--compact', useCompactPresentation);
      target.classList.toggle('customer-showcase-upgrades__grid--compact', useCompactPresentation);

      if (useCompactPresentation) {
        target.append(this.createCompactUpgradeList(entries));
      } else {
        entries.forEach(({ category: entryCategory, title, response }) => {
          target.append(this.createUpgradeCard(entryCategory, title, response));
        });
      }

      this.updateUpgradeVisibility(entries.length > 0);
    }

    createNarrativeBuildStory(paragraphs, fallbackText) {
      const story = document.createElement('customer-build-story');
      story.className = 'customer-build-about__body';
      const viewport = document.createElement('div');
      viewport.className = 'customer-build-about__viewport';
      const copy = document.createElement('div');
      copy.className = 'customer-build-about__copy';

      if (paragraphs.length) {
        paragraphs.forEach((sourceParagraph) => {
          const text = normalizeUpgradeText(sourceParagraph.textContent);
          if (!text) return;
          const paragraph = document.createElement('p');
          paragraph.textContent = text;
          copy.append(paragraph);
        });
      } else if (fallbackText) {
        const paragraph = document.createElement('p');
        paragraph.textContent = fallbackText;
        copy.append(paragraph);
      }

      const initial = document.createElement('span');
      initial.className = 'customer-build-about__initial';
      initial.setAttribute('aria-hidden', 'true');
      initial.textContent = copy.textContent.trim().match(/\p{L}/u)?.[0]?.toLocaleUpperCase() || '';
      viewport.append(copy);
      story.append(initial, viewport);
      return story;
    }

    createCompactUpgradeList(entries) {
      const wrapper = document.createElement('div');
      wrapper.className = 'customer-upgrade-sheet';
      const groups = new Map();
      const initialItems = Math.max(1, Number.parseInt(this.dataset.customerCompactInitialItems || '12', 10));
      let renderedItems = 0;

      entries.forEach((entry) => {
        const groupName = entry.category || 'Current upgrades';
        if (!groups.has(groupName)) groups.set(groupName, []);
        groups.get(groupName).push(entry);
      });

      groups.forEach((groupEntries, groupName) => {
        const group = document.createElement('section');
        group.className = 'customer-upgrade-sheet__group';

        const heading = document.createElement('h3');
        heading.className = 'customer-upgrade-sheet__heading';
        const headingText = document.createElement('span');
        headingText.textContent = groupName.replace(/\s*upgrades?$/i, '') || 'Current';
        const count = document.createElement('small');
        count.textContent = `${groupEntries.length} ${groupEntries.length === 1 ? 'upgrade' : 'upgrades'}`;
        heading.append(headingText, count);

        const list = document.createElement('ul');
        list.className = 'customer-upgrade-sheet__list';

        groupEntries.forEach((entry) => {
          const item = document.createElement('li');
          item.className = 'customer-upgrade-sheet__item';
          if (renderedItems >= initialItems) {
            item.hidden = true;
            item.dataset.customerCompactExtra = '';
          }

          if (entry.isCurrentUpgrades || entry.title.toLowerCase() === entry.response.toLowerCase()) {
            item.textContent = entry.response;
          } else {
            const title = document.createElement('strong');
            title.textContent = entry.title;
            const response = document.createElement('span');
            response.textContent = entry.response;
            item.append(title, response);
          }

          list.append(item);
          renderedItems += 1;
        });

        group.append(heading, list);
        wrapper.append(group);
      });

      if (entries.length > initialItems) {
        const toggle = document.createElement('button');
        toggle.className = 'customer-upgrade-sheet__toggle';
        toggle.type = 'button';
        toggle.setAttribute('aria-expanded', 'false');
        toggle.textContent = `VIEW ALL ${entries.length} UPGRADES`;
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          wrapper.querySelectorAll('[data-customer-compact-extra]').forEach((item) => {
            item.hidden = expanded;
          });
          toggle.setAttribute('aria-expanded', String(!expanded));
          toggle.textContent = expanded ? `VIEW ALL ${entries.length} UPGRADES` : 'SHOW FEWER UPGRADES';
          if (expanded) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        wrapper.append(toggle);
      }

      return wrapper;
    }

    updateUpgradeVisibility(hasUpgrades) {
      if (this.matches('customer-showcase-upgrades')) {
        this.hidden = !hasUpgrades;
        const cards = this.closest('[data-customer-upgrade-cards]');
        if (cards) cards.hidden = !hasUpgrades;
        const contentId = this.dataset.customerUpgrades;
        document.querySelectorAll('[data-customer-story]').forEach((story) => {
          if (!contentId || story.dataset.customerStory === contentId) story.hidden = hasUpgrades;
        });
        return;
      }

      this.classList.toggle('customer-showcase--has-upgrades', hasUpgrades);
    }

    createUpgradeCard(category, title, response) {
      const card = document.createElement('article');
      card.className = 'customer-upgrade-card';

      const icon = document.createElement('div');
      icon.className = 'customer-upgrade-card__icon';
      icon.setAttribute('aria-hidden', 'true');
      const iconImage = document.createElement('img');
      iconImage.src = this.dataset[this.iconForCategory(category)];
      iconImage.alt = '';
      iconImage.width = 58;
      iconImage.height = 58;
      icon.appendChild(iconImage);

      const categoryElement = document.createElement('p');
      categoryElement.className = 'customer-upgrade-card__category';
      categoryElement.textContent = `${category.replace(/\s*upgrades?$/i, '')} upgrades`;

      const titleElement = document.createElement('h3');
      titleElement.className = 'customer-upgrade-card__title';
      titleElement.textContent = title;

      const customerName = this.dataset.customerName || 'Owner';
      const customerFirstName = customerName.trim().split(/\s+/)[0] || 'Owner';
      const storedCopy = this.storedCopyFor(category, title, response);
      const setup = this.createDetail(`${customerFirstName}'s setup`, response, 'setup');
      card.append(icon, categoryElement, titleElement, setup);
      if (storedCopy) {
        card.append(this.createDetail('WHAT IT DOES', storedCopy, 'does'));
      }
      return card;
    }

    readStoredUpgradeCopy() {
      const source = this.querySelector('[data-customer-what-it-does]');
      if (!source?.textContent.trim()) return new Map();

      try {
        let data = JSON.parse(source.textContent);
        if (typeof data === 'string') data = JSON.parse(data);
        if (data?.version !== 1 || !Array.isArray(data.upgrades)) return new Map();

        return new Map(data.upgrades.flatMap((entry) => {
          const category = normalizeUpgradeText(entry?.category);
          const title = normalizeUpgradeText(entry?.title);
          const response = normalizeUpgradeText(entry?.customer_response);
          const copy = normalizeUpgradeText(entry?.what_it_does);
          if (!category || !title || !response || !copy) return [];
          return [[this.storedCopyKey(category, title, response), copy]];
        }));
      } catch (error) {
        console.warn('[DPP] Unable to read stored showcase explanations', error);
        return new Map();
      }
    }

    storedCopyKey(category, title, response) {
      return [category, title, response]
        .map((value) => normalizeUpgradeText(value).toLowerCase())
        .join('\u0000');
    }

    storedCopyFor(category, title, response) {
      return this.storedUpgradeCopy?.get(this.storedCopyKey(category, title, response)) || null;
    }

    createDetail(heading, copy, modifier) {
      const detail = document.createElement('div');
      detail.className = `customer-upgrade-card__detail customer-upgrade-card__detail--${modifier}`;
      const title = document.createElement('p');
      title.className = 'customer-upgrade-card__detail-heading';
      title.textContent = heading;
      if (modifier === 'does') {
        const arrow = document.createElement('span');
        arrow.className = 'customer-upgrade-card__detail-arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.innerHTML = '<svg viewBox="0 0 18 12"><path d="M1 6h14M10 1l5 5-5 5"/></svg>';
        title.append(arrow);
      }
      const body = document.createElement('p');
      body.textContent = copy;
      detail.append(title, body);
      return detail;
    }

    iconForCategory(category) {
      const normalized = normalizeCategory(category);
      if (normalized.includes('interior')) return 'iconInterior';
      if (normalized.includes('exterior') || normalized.includes('armor')) return 'iconExterior';
      if (normalized.includes('suspension') || normalized.includes('chassis')) return 'iconSuspension';
      if (normalized.includes('drivetrain') || normalized.includes('transmission')) return 'iconTransmission';
      if (normalized.includes('engine')) return 'iconEngine';
      if (normalized.includes('wheel') || normalized.includes('tire')) return 'iconWheels';
      return 'iconMisc';
    }
  }

  let storyId = 0;
  class CustomerBuildStory extends HTMLElement {
    connectedCallback() {
      const viewport = this.querySelector('.customer-build-about__viewport');
      const copy = this.querySelector('.customer-build-about__copy');
      if (!viewport || !copy) return;
      this.querySelector('.customer-build-about__toggle')?.remove();
      viewport.id ||= `CustomerBuildStory-${++storyId}`;
      const toggle = document.createElement('button');
      toggle.className = 'customer-build-about__toggle';
      toggle.type = 'button';
      toggle.textContent = 'View More';
      toggle.hidden = true;
      toggle.setAttribute('aria-controls', viewport.id);
      toggle.setAttribute('aria-expanded', 'false');
      this.append(toggle);
      let expanded = false;
      const update = () => {
        const overflows = copy.getBoundingClientRect().height > 400;
        this.classList.toggle('is-collapsed', overflows && !expanded);
        toggle.hidden = !overflows;
        toggle.setAttribute('aria-expanded', String(expanded || !overflows));
        toggle.textContent = expanded ? 'View Less' : 'View More';
      };
      toggle.addEventListener('click', () => {
        expanded = !expanded;
        update();
        if (!expanded) this.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
      this.storyObserver?.disconnect();
      this.storyObserver = new ResizeObserver(update);
      this.storyObserver.observe(copy);
      update();
    }

    disconnectedCallback() {
      this.storyObserver?.disconnect();
    }
  }

  if (!customElements.get('customer-build-story')) {
    customElements.define('customer-build-story', CustomerBuildStory);
  }

  if (!customElements.get('customer-showcase-page')) {
    customElements.define('customer-showcase-page', CustomerShowcasePage);
  }

  class CustomerShowcaseGallery extends HTMLElement {
    connectedCallback() {
      CustomerShowcasePage.prototype.setupGallery.call(this);
    }
  }

  class CustomerShowcaseDetails extends HTMLElement {
    connectedCallback() {
      CustomerShowcasePage.prototype.setupTitleFitting.call(this);
    }

    disconnectedCallback() {
      if (this.handleTextResize) window.removeEventListener('resize', this.handleTextResize);
      this.textResizeObserver?.disconnect();
      if (this.textFitFrame) cancelAnimationFrame(this.textFitFrame);
    }
  }

  class CustomerShowcaseUpgrades extends CustomerShowcasePage {
    connectedCallback() {
      this.setupUpgradeCards();
    }
  }

  if (!customElements.get('customer-showcase-gallery')) {
    customElements.define('customer-showcase-gallery', CustomerShowcaseGallery);
  }
  if (!customElements.get('customer-showcase-details')) {
    customElements.define('customer-showcase-details', CustomerShowcaseDetails);
  }
  if (!customElements.get('customer-showcase-upgrades')) {
    customElements.define('customer-showcase-upgrades', CustomerShowcaseUpgrades);
  }
})();
